import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { getUserAvatarUrl, handleUserAvatarError } from '../utils/avatars';
import { getViolationRiderName } from '../utils/violations';
import './EventInviteModal.css';

// «Поделиться тренировкой»: выбор пользователей, каждому уходит своя копия
// с отметкой, от кого она, и уведомление (Api::WorkoutSharesController).
// Устроено как окно приглашений в событие (EventInviteModal) и берет его стили:
// поиск с паузой, подгрузка страниц при прокрутке, выбор сохраняется между страницами
const WorkoutShareModal = ({ workout, onClose }) => {
  const { t, language } = useLanguage();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState(new Set());
  // Получившие в этом окне — отмечаем без перезагрузки списка
  const [sentIds, setSentIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const bodyRef = useRef(null);
  const sentinelRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    let cancelled = false;

    const fetchUsers = async () => {
      setLoading(true);
      setError('');

      try {
        const params = { page };
        if (search) params.q = search;
        const response = await axios.get(`/api/workouts/${workout.id}/shares`, { params });
        if (cancelled) return;

        const loaded = response.data.users || [];
        const { current_page: current, total_pages: totalPages, total_count: total } = response.data.pagination;
        setUsers((prev) => {
          if (page === 1) return loaded;
          const known = new Set(prev.map((u) => u.id));
          return [...prev, ...loaded.filter((u) => !known.has(u.id))];
        });
        setHasMore(current < totalPages);
        setTotalCount(total);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('workouts.share.loadError'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchUsers();
    return () => { cancelled = true; };
  }, [workout.id, page, search, t]);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [search]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading || !hasMore || error) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setPage((p) => p + 1);
      },
      { root: bodyRef.current, rootMargin: '0px 0px 120px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, hasMore, error, users.length]);

  const isShared = (user) => user.shared || sentIds.has(user.id);
  const initialLoading = loading && page === 1;

  const toggleUser = (userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId); else next.add(userId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0) return;

    setSubmitting(true);
    setError('');
    setResult('');

    try {
      const ids = Array.from(selected);
      const response = await axios.post(`/api/workouts/${workout.id}/shares`, { user_ids: ids });
      setSentIds((prev) => new Set([...prev, ...ids]));
      setSelected(new Set());
      setResult(response.data.message);
    } catch (err) {
      setError(err.response?.data?.error || t('workouts.share.sendError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div className="event-invite-overlay" onClick={handleOverlayClick}>
      <div className="event-invite-modal" role="dialog" aria-modal="true" aria-label={t('workouts.share.title')}>
        <div className="event-invite-header">
          <div>
            <h2>{t('workouts.share.title')}</h2>
            <div className="event-invite-subtitle">{workout.name}</div>
          </div>
          <button type="button" className="event-invite-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>

        <div className="event-invite-toolbar">
          <div className="event-invite-hint">{t('workouts.share.hint')}</div>
          <input
            type="search"
            className="event-invite-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('workouts.share.searchPlaceholder')}
            autoFocus
          />
          <div className="event-invite-list-meta">
            <span />
            {!initialLoading && totalCount > 0 && (
              <span className="event-invite-found">{t('workouts.share.found')}: {totalCount}</span>
            )}
          </div>
        </div>

        <div className="event-invite-body" ref={bodyRef}>
          {initialLoading ? (
            <div className="event-invite-message">{t('common.loading')}</div>
          ) : users.length === 0 ? (
            <div className="event-invite-message">{t('workouts.share.notFound')}</div>
          ) : (
            users.map((user) => {
              const shared = isShared(user);
              return (
                <label key={user.id} className={`event-invite-item ${shared ? 'event-invite-item-invited' : ''}`}>
                  <input
                    type="checkbox"
                    checked={shared || selected.has(user.id)}
                    disabled={shared}
                    onChange={() => toggleUser(user.id)}
                  />
                  <img
                    src={getUserAvatarUrl(user.avatar)}
                    onError={handleUserAvatarError}
                    alt=""
                    className="event-invite-avatar"
                  />
                  <span className="event-invite-user">
                    <span className="event-invite-name">{getViolationRiderName(user, language)}</span>
                    <span className="event-invite-nickname">@{user.nickname}</span>
                  </span>
                  {shared && <span className="event-invite-badge">{t('workouts.share.alreadySent')}</span>}
                </label>
              );
            })
          )}
          {!initialLoading && users.length > 0 && (
            <>
              {loading && <div className="event-invite-message">{t('common.loading')}</div>}
              <div ref={sentinelRef} className="event-invite-sentinel" />
            </>
          )}
        </div>

        <div className="event-invite-footer">
          <div className="event-invite-status">
            {error ? (
              <span className="event-invite-error">{error}</span>
            ) : result ? (
              <span className="event-invite-result">{result}</span>
            ) : (
              <span>{t('workouts.share.selected')}: {selected.size}</span>
            )}
          </div>
          <button
            type="button"
            className="event-invite-submit"
            onClick={handleSubmit}
            disabled={submitting || selected.size === 0}
          >
            {submitting ? t('workouts.share.sending') : t('workouts.share.send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkoutShareModal;
